import { cn } from "@productlane/lib/src/cn"
import { typographyVariants } from "@productlane/ui/src/components/typography"

export interface Option {
	n: number
	label: string
	note: string
	spec: string
	body: string
}

export interface BoxTone {
	n: number
	label: string
	note: string
	surface: string
}

// What ships today. The wrapper asks for text-primary, then the editor variant
// overrides every paragraph and list item back to text-secondary.
const SHIPPED = cn(
	"pl-ai-content min-w-0 !text-[0.95rem] tracking-tight text-primary [overflow-wrap:anywhere] [&>*:first-child]:!mt-0",
	typographyVariants({ variant: "editor" }),
)

// .prose.editor-typography p carries line-height !important, so an override
// needs three classes of specificity to land.
const DARK_BODY =
	"prose-p:text-primary prose-li:text-primary prose-td:text-primary"

export const WIDGET_CHAT_TYPOGRAPHY_OPTIONS: Option[] = [
	{
		n: 1,
		label: "Shipped today",
		note: "prose-p:text-secondary in the editor variant paints every paragraph gray-500. The same variant drives the widget bubble, the help center article, and the FAQ box, so all three read grey.",
		spec: "gray-500 · 400 · 15.2px · 1.55 · tracking-tight",
		body: SHIPPED,
	},
	{
		n: 2,
		label: "Body in primary",
		note: "Drop the secondary override so paragraphs and list items inherit text-primary. Nothing else moves: same size, weight, leading.",
		spec: "gray-900 · 400 · 15.2px · 1.55 · tracking-tight",
		body: cn(SHIPPED, DARK_BODY),
	},
	{
		n: 3,
		label: "Primary, 450, normal tracking",
		note: "Closest to the reference. Dark body, a half step of weight, tracking back to normal so the letters stop crowding at 15px, and bold at 600.",
		spec: "gray-900 · 450 · 15.2px · 1.6 · tracking-normal",
		body: cn(
			SHIPPED,
			DARK_BODY,
			"prose-p:font-[450] prose-li:font-[450] prose-strong:font-[600]",
			"prose-p:tracking-normal prose-li:tracking-normal",
			"[&.prose.editor-typography_p]:!leading-[1.6] [&_li]:!leading-[1.6]",
		),
	},
	{
		n: 4,
		label: "Primary, 450, 16px",
		note: "Option 3 at a full 16px with more air between paragraphs. Costs roughly one extra line per answer in a 400px widget column.",
		spec: "gray-900 · 450 · 16px · 1.6 · tracking-normal",
		body: cn(
			SHIPPED,
			DARK_BODY,
			"!text-[1rem] prose-p:font-[450] prose-li:font-[450] prose-strong:font-[600]",
			"prose-p:tracking-normal prose-li:tracking-normal",
			"[&.prose.editor-typography_p]:!leading-[1.6] [&_li]:!leading-[1.6]",
			"[&>p:not(:first-child)]:!mt-[1.15rem] [&_p:not(:first-child)]:!mt-[1.15rem]",
		),
	},
	{
		n: 5,
		label: "Primary, 400, open leading",
		note: "Dark body at the current weight. The contrast comes from leading and a heavier bold instead, so Windows never renders it muddy.",
		spec: "gray-900 · 400 · 16px · 1.7 · tracking-normal · bold 650",
		body: cn(
			SHIPPED,
			DARK_BODY,
			"!text-[1rem] prose-strong:font-[650]",
			"prose-p:tracking-normal prose-li:tracking-normal",
			"[&.prose.editor-typography_p]:!leading-[1.7] [&_li]:!leading-[1.7]",
		),
	},
]

export const FAQ_BOX_TONES: BoxTone[] = [
	{
		n: 1,
		label: "Shipped today",
		note: "bg-gray-100 with a gray-300 border. The fill sits close enough to the border that the box reads as one solid grey slab.",
		surface:
			"border-gray-300 bg-gray-100 dark:border-gray-700 dark:bg-gray-800",
	},
	{
		n: 2,
		label: "One tone lighter",
		note: "bg-gray-50 with a gray-200 border. The box still groups the answer, and the text sits on near-white.",
		surface:
			"border-gray-200 bg-gray-50 dark:border-gray-700/80 dark:bg-gray-850",
	},
	{
		n: 3,
		label: "Outline only",
		note: "White fill, gray-200 border. The card is drawn by its edge, so an open answer reads at the same contrast as the rest of the article.",
		surface:
			"border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-900",
	},
]
